/**
 * Common event media fetching service.
 *
 * Two-step flow for optimal UX:
 *   1. fetchEventMedia  — fast EVM call → thumbnail URL + video URL + timing
 *   2. fetchEventVideo  — Phoenix transcoding call → blob object URL (guaranteed playable)
 *
 * The caller shows the thumbnail immediately from step 1, then optionally
 * uses the EVM videoUrl directly or calls step 2 for a transcoded blob.
 *
 * IMPORTANT: Callers must call URL.revokeObjectURL() on the blob URL from
 * fetchEventVideo when the video is no longer needed to prevent memory leaks.
 *
 * Adapted from RingAILabWebsite/src/services/event-media-service.ts
 */

import { AppApiGatewayService } from './appApiGatewayService';

// ─── Public types ──────────────────────────────────────────────────────────

export interface EventMedia {
  thumbnailUrl: string | null;
  videoUrl: string | null;
  deviceId: string;
  startTimestampMs: number;
  durationMs: number;
}

export interface VideoOptions {
  codec?: string;
  frameRate?: number;
  resolution?: { width: number; height: number };
  audioEnabled?: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────

const DEFAULT_VIDEO_OPTIONS: Required<VideoOptions> = {
  codec: 'avc',
  frameRate: 15,
  resolution: { width: 640, height: 480 },
  audioEnabled: true,
};

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * Check whether a video URL from EVM is directly playable in an HTML <video> tag.
 * Currently playable URLs are Phoenix-hosted transcoded .mp4 files.
 * If this returns false, use fetchEventVideo() to get a playable blob URL.
 */
export function isVideoUrlPlayable(url: string | null): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.hostname.includes('phoenix') && parsed.pathname.endsWith('.mp4');
  } catch {
    return false;
  }
}

/**
 * Extract region from a Phoenix download URL hostname.
 * e.g. "download-us-west-2.prod.phoenix.ring.devices.a2z.com" → "us-west-2"
 */
function extractRegion(hostname: string): string {
  const match = /download-([^.]+)\./.exec(hostname);
  return match ? match[1] : 'unknown';
}

// ─── fetchEventMedia ───────────────────────────────────────────────────────

/**
 * Fetch event metadata from EVM including thumbnail and video URLs.
 * This is a fast call — use it to show the thumbnail immediately.
 */
export async function fetchEventMedia(
  service: AppApiGatewayService,
  eventId: string,
): Promise<EventMedia> {
  const response = await service.fetchEVMEvent(eventId);

  if (response.status !== 200 || !response.data) {
    throw new Error(`Failed to fetch event media for ${eventId} (${response.status})`);
  }

  const event = response.data as {
    event_id: string;
    source_id: string;
    start_time: string;
    duration_ms: number;
    visualizations?: {
      cloud_media_visualization?: {
        media: Array<{ url?: string; file_type?: string }>;
      };
    };
  };

  const medias = event.visualizations?.cloud_media_visualization?.media ?? [];

  const thumbnailUrl = medias.find((m) => m.file_type === 'THUMBNAIL')?.url ?? null;
  const rawVideoUrl = medias.find((m) => m.file_type === 'VIDEO')?.url ?? null;

  // Only resolve through Phoenix proxy if the URL is a playable Phoenix .mp4.
  // Non-playable URLs (e.g. S3) can't be resolved here — the caller should
  // use fetchEventVideo() to get a transcoded blob instead.
  let videoUrl: string | null = null;
  if (isVideoUrlPlayable(rawVideoUrl)) {
    const parsed = new URL(rawVideoUrl!);
    const regionMatch = parsed.hostname.match(/download-([^.]+)\./);
    const region = regionMatch ? regionMatch[1] : 'unknown';
    const path = parsed.pathname + parsed.search;

    const baseUrl = service.clientConfig.invokeUrl;
    const phoenixResponse = await fetch(`${baseUrl}/phoenix-${region}${path}`, {
      method: 'GET',
    });

    const location = phoenixResponse.headers.get('location');
    videoUrl = (phoenixResponse.status === 200 && location) ? location : null;
  }

  return {
    thumbnailUrl,
    videoUrl,
    deviceId: event.source_id,
    startTimestampMs: new Date(event.start_time).getTime(),
    durationMs: event.duration_ms,
  };
}

// ─── fetchEventVideo ───────────────────────────────────────────────────────

/**
 * Fetch a transcoded video from Phoenix via the backend proxy.
 * Always returns a blob object URL that is guaranteed playable in <video>.
 *
 * The backend calls Phoenix, receives a redirect, and returns `{ video_url }`.
 * We then fetch that URL as a blob because it doesn't support range requests.
 */
export async function fetchEventVideo(
  service: AppApiGatewayService,
  deviceId: string,
  startTimestampMs: number,
  durationMs: number,
  options?: VideoOptions,
): Promise<string> {
  const opts = { ...DEFAULT_VIDEO_OPTIONS, ...options };

  // Step 1: get the download URL from our backend proxy (auth required).
  const response = await service.fetchVideoDownloadUrl({
    device_id: deviceId,
    timestamp: startTimestampMs,
    duration: durationMs,
    codec: opts.codec,
    frame_rate: opts.frameRate,
    width: opts.resolution.width,
    height: opts.resolution.height,
    audio_enabled: opts.audioEnabled,
  });

  if (response.status !== 200 || !response.data?.video_url) {
    throw new Error(
      `Failed to fetch event video for device ${deviceId} (${response.status})`,
    );
  }

  const videoDownloadUrl = response.data.video_url as string;

  // Step 2: fetch the actual video bytes as a blob.
  // /video-* is routed by CloudFront directly — no /capigw auth needed.
  const parsed = new URL(videoDownloadUrl);
  const region = extractRegion(parsed.hostname);
  const path = parsed.pathname + parsed.search;

  const baseUrl = service.clientConfig.invokeUrl;
  const videoResponse = await fetch(`${baseUrl}/video-${region}${path}`, {
    method: 'GET',
  });

  if (!videoResponse.ok) {
    throw new Error(`Failed to download video blob (${videoResponse.status})`);
  }

  const blob = await videoResponse.blob();
  return URL.createObjectURL(blob);
}
